import ServiceWrapper, { type ServiceOptions } from "@utils/ServiceWrapper";

import { TOKEN_URL, TOKEN_URL_DEV } from "./config";

// For type safety, define the explicit actions this DAO can perform.
type AuthAction = "tokenRead" | "tokenReadDev";

interface AuthDaoOptions {
    action: AuthAction;
    username?: string | null;
}

/**
 * Data Access for the Auth API.
 * Uses the actionConfig pattern from VOE.
 */
const AuthDao = <T>(options: AuthDaoOptions): Promise<{ data: T }> => {
    if (!options || typeof options !== "object") {
        // Using throw to allow for centralized error handling in the calling service.
        throw new Error("AuthDao: Invalid options parameter provided.");
    }

    const { action, username } = options;

    if (!action) {
        throw new Error("AuthDao: Missing required 'action' parameter.");
    }

    const requestOptions: ServiceOptions = {
        url: "", // url is provided by config, but required by ServiceOptions type
        method: "GET",
        headers: { "Cache-Control": "no-cache" }
    };

    const actionConfig: Record<AuthAction, Partial<ServiceOptions>> = {
        tokenRead: { method: "GET", url: TOKEN_URL },
        tokenReadDev: { method: "GET", url: `${TOKEN_URL_DEV ?? ""}/${username}/token` }
    };

    const config = actionConfig[action];
    if (!config) {
        // This check is for runtime safety, in case an invalid action string is passed.
        throw new Error(`AuthDao: Invalid action "${action}" provided.`);
    }

    if (action === "tokenReadDev" && !username) {
        throw new Error("AuthDao: Missing username for 'tokenReadDev' action.");
    }

    return ServiceWrapper.serviceCall({
        options: {
            ...requestOptions,
            ...config
        }
    }) as Promise<{ data: T }>;
};

export default AuthDao;
